import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import '../scss/teamSelection.scss';
import Header from '../Header';
import TeamDisplay from './match/TeamDisplay';


const TeamSelection = ({ hideHeader = false }) => {
    const navigate = useNavigate();
    const { matchId } = useParams();
    const [playerData, setPlayerData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [teams, setTeams] = useState(null);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [fromCache, setFromCache] = useState(false);
    const [activeTab, setActiveTab] = useState('overview');
    const [cacheDate, setCacheDate] = useState(null);
    const [isAdmin, setIsAdmin] = useState(false);

    const { tab, subtab } = useParams();

    useEffect(() => {
        // Update activeTab when URL params change
        if (subtab) {
            setActiveTab(subtab);
        }
    }, [subtab]);

    const handleSubTabChange = (newSubTab) => {
        setActiveTab(newSubTab);
        navigate(`/match/${matchId}/teams/${newSubTab}`, { replace: true });
    };

    const validateTeamData = (data) => {
        // Check basic structure
        if (!data || typeof data !== 'object') return false;
        if (!data.team_white || !data.team_colour) return false;

        // Required team properties
        const requiredTeamProps = [
            'defense', 'midfield', 'attack', 
            'formation', 'average_skill', 
            'attack_rating', 'defense_rating'
        ];

        // Required player properties
        const requiredPlayerProps = [
            'name', 'position', 'skill_rating', 
            'playing_role', 'player_id'
        ];

        for (const team of [data.team_white, data.team_colour]) {
            // Check team properties
            if (!requiredTeamProps.every(prop => prop in team)) return false;

            // Check player arrays
            for (const section of ['defense', 'midfield', 'attack']) {
                if (!Array.isArray(team[section])) return false;
                
                // Check each player's properties
                for (const player of team[section]) {
                    if (!requiredPlayerProps.every(prop => prop in player)) {
                        console.log('Missing player property in:', player);
                        return false;
                    }
                }
            }
        }

        return true;
    };

    const extractJsonFromResponse = (response) => {
        // Helper function to clean JSON string
        const cleanJsonString = (str) => {
            try {
                // Remove any markdown backticks and language identifiers
                str = str.replace(/```json\s*|\s*```/g, '');
                
                // Remove any trailing commas before closing brackets/braces
                str = str.replace(/,(\s*[}\]])/g, '$1');
                
                // Fix any double quotes within strings that aren't already escaped
                //str = str.replace(/(?<!\\)"|"|"/g, '"');
                
                // Remove any control characters
                str = str.replace(/[\x00-\x1F\x7F-\x9F]/g, '');

                // Fix any single quotes used instead of double quotes
                str = str.replace(/'/g, '"');

                // Remove any extra whitespace between properties
                str = str.replace(/\s+/g, ' ');

                // Fix any missing quotes around property names
                str = str.replace(/([{,]\s*)([a-zA-Z_][a-zA-Z0-9_]*)\s*:/g, '$1"$2":');

                // Remove any comments
                str = str.replace(/\/\*[\s\S]*?\*\/|\/\/.*/g, '');

                // Ensure numbers are properly formatted
                str = str.replace(/:\s*(\d+\.?\d*)\s*([,}])/g, ': $1$2');

                return str.trim();
            } catch (e) {
                console.error('Error in cleanJsonString:', e);
                return str;
            }
        };
        

        const isAdmin = sessionStorage.getItem('is_admin');  
        setIsAdmin(isAdmin);

        // If response is a string, try to extract JSON from markdown
        if (typeof response === 'string') {
            try {
                // Log the original response for debugging
                console.log('Original response:', response);

                // First try to parse it directly after cleaning
                const cleanedResponse = cleanJsonString(response);
                console.log('Cleaned response:', cleanedResponse);

                try {
                    const parsedData = JSON.parse(cleanedResponse);
                    if (validateTeamData(parsedData)) {
                        return parsedData;
                    }
                    throw new Error('Invalid team data structure');
                } catch (parseError) {
                    console.error('Initial parse error:', parseError);

                    // Try to find the JSON object in the response
                    const jsonStart = response.indexOf('{');
                    const jsonEnd = response.lastIndexOf('}') + 1;

                    if (jsonStart >= 0 && jsonEnd > jsonStart) {
                        const jsonStr = cleanJsonString(response.slice(jsonStart, jsonEnd));
                        console.log('Extracted JSON string:', jsonStr);

                        const extractedData = JSON.parse(jsonStr);
                        if (validateTeamData(extractedData)) {
                            return extractedData;
                        }
                    }

                    throw new Error('Could not extract valid team data');
                }
            } catch (error) {
                console.error('JSON processing error:', error);
                console.log('Failed response:', response);
                throw new Error('Failed to process team data');
            }
        }

        // If response is already an object, validate it
        if (validateTeamData(response)) {
            return response;
        }
        throw new Error('Invalid team data structure');
    };

    const fetchTeamData = async (refresh = false) => {
        try {
            setIsRefreshing(refresh);
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/get_team_selection_data.php?match_id=${matchId}${refresh ? '&refresh=true' : ''}`
            );
            const data = await response.json();
            
            if (!data.success) {
                throw new Error(data.message || 'Failed to fetch team data');
            }
            
            setPlayerData(data);
            setFromCache(data.from_cache || false);
            
            if (data.from_cache) {
                setCacheDate(data.cache_date);
            }
            
            try {
                if (!data.ai_suggestion) {
                    throw new Error('No AI suggestion received');
                }
                
                const teamsData = extractJsonFromResponse(data.ai_suggestion);
                
                // Validate team data structure
                if (!validateTeamData(teamsData)) {
                    console.error('Invalid team data structure:', teamsData);
                    throw new Error('Invalid team data structure');
                }
                
                console.log('teamsData', teamsData);
                setTeams(teamsData);

            } catch (parseError) {
                console.error('Error parsing AI suggestion:', parseError);
                setTeams(null);
                throw new Error('Failed to process team data');
            }
        } catch (error) {
            console.error('Error in fetchTeamData:', error);
            setTeams(null);
        } finally {
            setIsLoading(false);
            setIsRefreshing(false);
        }
    };

    useEffect(() => {
        fetchTeamData();
    }, [matchId]);

    const renderTabContent = () => {
        switch (activeTab) {
            case 'overview':
                return teams && (
                    <>
                        {teams.match_analysis && (
                            <div className="match-analysis">
                                <h3>Match Overview</h3>
                                {isAdmin === 'true' && (
                                    <div className="teams-comparison">
                                        <div className="teams-grid">
                                            <div className="team-stats white">
                                                <h4>Team White</h4>
                                                <div className="ratings">
                                                    <div className="rating">
                                                        <span className="label">Overall</span>
                                                        <span className="value">
                                                            {(teams.team_white.team_ratings?.overall || teams.team_white.average_skill)
                                                                ? Number(teams.team_white.team_ratings?.overall || teams.team_white.average_skill).toFixed(1)
                                                                : '-'}
                                                        </span>
                                                    </div>
                                                    <div className="rating">
                                                        <span className="label">Attack</span>
                                                        <span className="value">
                                                            {(teams.team_white.team_ratings?.attack || teams.team_white.attack_rating)
                                                                ? Number(teams.team_white.team_ratings?.attack || teams.team_white.attack_rating).toFixed(1)
                                                                : '-'}
                                                        </span>
                                                    </div>
                                                    <div className="rating">
                                                        <span className="label">Midfield</span>
                                                        <span className="value">
                                                            {(teams.team_white.team_ratings?.midfield || teams.team_white.midfield_rating)
                                                                ? Number(teams.team_white.team_ratings?.midfield || teams.team_white.midfield_rating).toFixed(1)
                                                                : '-'}
                                                        </span>
                                                    </div>
                                                    <div className="rating">
                                                        <span className="label">Defense</span>
                                                        <span className="value">
                                                            {(teams.team_white.team_ratings?.defense || teams.team_white.defense_rating)
                                                                ? Number(teams.team_white.team_ratings?.defense || teams.team_white.defense_rating).toFixed(1)
                                                                : '-'}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="team-stats colour">
                                                <h4>Team Colour</h4>
                                                <div className="ratings">
                                                    <div className="rating">
                                                        <span className="label">Overall</span>
                                                        <span className="value">
                                                            {(teams.team_colour.team_ratings?.overall || teams.team_colour.average_skill)
                                                                ? Number(teams.team_colour.team_ratings?.overall || teams.team_colour.average_skill).toFixed(1)
                                                                : '-'}
                                                        </span>
                                                    </div>
                                                    <div className="rating">
                                                        <span className="label">Attack</span>
                                                        <span className="value">
                                                            {(teams.team_colour.team_ratings?.attack || teams.team_colour.attack_rating)
                                                                ? Number(teams.team_colour.team_ratings?.attack || teams.team_colour.attack_rating).toFixed(1)
                                                                : '-'}
                                                        </span>
                                                    </div>
                                                    <div className="rating">
                                                        <span className="label">Midfield</span>
                                                        <span className="value">
                                                            {(teams.team_colour.team_ratings?.midfield || teams.team_colour.midfield_rating)
                                                                ? Number(teams.team_colour.team_ratings?.midfield || teams.team_colour.midfield_rating).toFixed(1)
                                                                : '-'}
                                                        </span>
                                                    </div>
                                                    <div className="rating">
                                                        <span className="label">Defense</span>
                                                        <span className="value">
                                                            {(teams.team_colour.team_ratings?.defense || teams.team_colour.defense_rating)
                                                                ? Number(teams.team_colour.team_ratings?.defense || teams.team_colour.defense_rating).toFixed(1)
                                                                : '-'}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                                <div className="analysis-grid">
                                    {teams.match_analysis.player_count_assessment && (
                                        <div className="analysis-item">
                                            <h4>Squad Status</h4>
                                            <p>{teams.match_analysis.player_count_assessment}</p>
                                        </div>
                                    )}
                                    {teams.match_analysis.form_highlights && (
                                        <div className="analysis-item">
                                            <h4>Form Highlights</h4>
                                            <ul>
                                                {teams.match_analysis.form_highlights.map((highlight, index) => (
                                                    <li key={index}>{highlight}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                    {teams.match_analysis.key_matchups && (
                                        <div className="analysis-item">
                                            <h4>Key Matchups</h4>
                                            <p>{teams.match_analysis.key_matchups}</p>
                                        </div>
                                    )}
                                    {teams.match_analysis.rotation_strategy && (
                                        <div className="analysis-item">
                                            <h4>Rotation Strategy</h4>
                                            <p>{teams.match_analysis.rotation_strategy}</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </>
                );
            case 'whites':
                return teams && (
                    <TeamDisplay 
                        team={teams.team_white} 
                        title="Team White" 
                        colorClass="white-team"
                        isAdmin={isAdmin}
                    />
                );
            case 'colours':
                return teams && (
                    <TeamDisplay 
                        team={teams.team_colour} 
                        title="Team Colour" 
                        colorClass="colour-team"
                        isAdmin={isAdmin}
                    />
                );
            default:
                return null;
        }
    };

    if (isLoading) return <div className="loading">Loading player data...</div>;
    if (!playerData) return <div className="error">Error loading player data</div>;

    return (
        <div className="team-selection container">
            {!hideHeader && (
                <>
                    <Header />
                    <div className="button-container">
                        <button className="back-button" onClick={() => navigate(-1)}>← Back</button>
                        <button 
                            className="refresh-button"
                            onClick={() => fetchTeamData(true)}
                            disabled={isRefreshing}
                        >
                            {isRefreshing ? 'Generating...' : 'Generate New Teams'}
                        </button>
                    </div>
                </>
            )}
            <h2>Team Selection</h2>
            {teams ? (
                <>
                    {/* {teams.tactical_notes && (
                        <div className="tactical-notes">
                            <p>{teams.tactical_notes}</p>
                        </div>
                    )} */}
                    <div className="tabs">
                        <button 
                            className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
                            onClick={() => handleSubTabChange('overview')}
                        >
                            Match Overview
                        </button>
                        <button 
                            className={`tab ${activeTab === 'whites' ? 'active' : ''}`}
                            onClick={() => handleSubTabChange('whites')}
                        >
                            Team White
                        </button>
                        <button 
                            className={`tab ${activeTab === 'colours' ? 'active' : ''}`}
                            onClick={() => handleSubTabChange('colours')}
                        >
                            Team Colour
                        </button>
                    </div>
                    <div className="tab-content">
                        {renderTabContent()}
                    </div>
                </>
            ) : (
                <div className="error-message">
                    <h3>Error Generating Teams</h3>
                    <p>There was an error processing the AI response. Please try again.</p>
                    <button 
                        className="retry-button"
                        onClick={() => fetchTeamData(true)}
                        disabled={isRefreshing}
                    >
                        Try Again
                    </button>
                </div>
            )}
            {fromCache && (
                <div className="cache-notice">
                   Cached data from {new Date(cacheDate).toLocaleString()}
                </div>
            )}
        </div>
    );
};

export default TeamSelection; 